import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Caption1, ConfirmDialog, EditIcon, IconButton, TrashIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import { getAllUsersIdsNamesArray } from "~/store/selectors/selector.users.jsx";
import FormatDate from "../Formatters/FormatDate.jsx";
import "./Reminder.css";
import { FormatSmartLocalDateTime } from "../Formatters/FormatSmartLocalDateTime.js";

const Reminder = ({ reminder, onDelete, onEdit }) => {
  const queryClient = useQueryClient();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { t } = useTranslation();

  const handleOpenDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  const handleDelete = useCallback(() => {
    queryClient.invalidateQueries(["notifications", "for-screen"]);
    handleCloseDeleteDialog();
    onDelete(reminder.id);
  }, [handleCloseDeleteDialog, onDelete, queryClient, reminder.id]);

  const users = useSelector(getAllUsersIdsNamesArray);

  const subscribersContent = useMemo(() => {
    const subscribers = users.filter((user) => reminder?.subscribers?.includes(user.id)).map((user) => user.name);
    return subscribers.join(", ");
  }, [reminder?.subscribers, users]);

  const onEditCallback = useCallback(() => {
    onEdit(reminder.id);
  }, [onEdit, reminder.id]);

  return (
    <>
      <div className="tw-flex tw-p-4 tw-flex-col tw-justify-center tw-items-start tw-self-stretch tw-gap-2 tw-bg-page-paper-main tw-relative tw-border tw-border-solid tw-border-theme-400 ac-border-radius-8 reminder-wrapper">
        <div className="tw-items-center tw-gap-1 tw-absolute tw-right-2 tw-top-2 reminder-actions">
          <IconButton
            type="button"
            variant="text gray"
            size="small"
            onClick={onEditCallback}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            type="button"
            variant="text gray"
            size="small"
            onClick={handleOpenDeleteDialog}
          >
            <TrashIcon />
          </IconButton>
        </div>

        <div
          data-testid="send-on-date"
          className="tw-flex tw-items-center tw-flex-1 tw-px-0.5"
        >
          <Caption1 letterSpacing="regular">{t("Date")}:&nbsp;</Caption1>
          <Caption1
            letterSpacing="regular"
            color="secondary"
          >
            {reminder.send_on ? (
              <FormatDate value={reminder.send_on} />
            ) : (
              <FormatSmartLocalDateTime value={reminder.remind_on} />
            )}
          </Caption1>
        </div>
        <div
          data-testid="subscribers-content"
          className="tw-flex tw-items-center tw-flex-1 tw-px-0.5"
        >
          <Caption1
            letterSpacing="regular"
            lineHeight="loose"
          >
            {t("A reminder will be sent to")}:&nbsp;
            <span className="tw-text-theme-700">{subscribersContent}</span>
          </Caption1>
        </div>
      </div>
      {openDeleteDialog ? (
        <ConfirmDialog
          open={openDeleteDialog}
          dialogTitle={t("Delete Reminder")}
          dialogContent={t("This reminder will be deleted permanently.")}
          confirmBtnText={t("Delete")}
          cancelBtnText={t("Cancel")}
          onConfirm={handleDelete}
          onCancel={handleCloseDeleteDialog}
        />
      ) : null}
    </>
  );
};

Reminder.propTypes = {
  reminder: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
};

Reminder.defaultProps = {
  onDelete: () => null,
  onEdit: () => null,
};

export default React.memo(Reminder);
